export interface IndicatorRect {
    bottom: number;
    height: number;
    left: number;
    right: number;
    top: number;
    width: number;
}

export interface IndicatorItemMeasurement {
    marginBottom: number;
    marginLeft: number;
    marginRight: number;
    marginTop: number;
    rect: IndicatorRect;
}

export interface InsertionIndicatorLayout {
    height: number;
    left: number;
    top: number;
    width: number;
}

const resolveVerticalCrossAxisLayout = (
    containerRect: IndicatorRect,
    dragged: IndicatorItemMeasurement,
    nextItem?: IndicatorItemMeasurement,
    previousItem?: IndicatorItemMeasurement,
): Pick<InsertionIndicatorLayout, 'left' | 'width'> => {
    // The drag engine portals the active item and may give that fixed-position
    // clone an intrinsic width. A vertical insertion marker belongs to the
    // destination lane, so prefer a stationary sibling's border box and fall
    // back to the container's usable width when the lane has no siblings.
    const destinationItem = nextItem ?? previousItem;

    if (destinationItem) {
        return {
            left: destinationItem.rect.left,
            width: destinationItem.rect.width,
        };
    }

    const left = containerRect.left + dragged.marginLeft;
    const availableWidth = Math.max(0, containerRect.right - left - dragged.marginRight);

    return {
        left,
        width: Math.min(dragged.rect.width, availableWidth) || availableWidth,
    };
};

interface ResolveInsertionIndicatorLayoutOptions {
    containerRect: IndicatorRect;
    destinationIndex: number;
    direction: 'horizontal' | 'vertical';
    dragged: IndicatorItemMeasurement;
    gap?: number;
    items: IndicatorItemMeasurement[];
    paddingStart?: number;
}

/**
 * Resolves the unoccupied slot created by the drag engine after it transforms
 * neighboring items. Margins and container gaps remain outside the visual
 * marker so its outline maps one-to-one to the dragged component's border box.
 */
export const resolveInsertionIndicatorLayout = ({
    containerRect,
    destinationIndex,
    direction,
    dragged,
    gap = 0,
    items,
    paddingStart = 0,
}: ResolveInsertionIndicatorLayoutOptions): InsertionIndicatorLayout => {
    if (direction === 'horizontal') {
        const sortedItems = [...items].sort((left, right) => left.rect.left - right.rect.left);
        const nextItem = sortedItems[destinationIndex];
        const previousItem =
            destinationIndex > 0 ? sortedItems[Math.min(destinationIndex - 1, sortedItems.length - 1)] : undefined;
        const sharedGutter = Math.max(
            gap,
            ...items.flatMap((item) => [item.marginLeft, item.marginRight]),
            dragged.marginLeft,
            dragged.marginRight,
        );
        const left =
            destinationIndex === 0
                ? containerRect.left + paddingStart
                : previousItem
                  ? previousItem.rect.right + sharedGutter
                  : containerRect.left + paddingStart;

        return {
            height: dragged.rect.height,
            left,
            top: nextItem?.rect.top ?? previousItem?.rect.top ?? containerRect.top,
            width: dragged.rect.width,
        };
    }

    const sortedItems = [...items].sort((left, right) => left.rect.top - right.rect.top);
    const nextItem = sortedItems[destinationIndex];
    const previousItem = sortedItems.at(-1);
    const top = nextItem
        ? nextItem.rect.top - nextItem.marginTop - gap - dragged.rect.height - dragged.marginBottom
        : previousItem
          ? previousItem.rect.bottom + previousItem.marginBottom + gap + dragged.marginTop
          : containerRect.top + paddingStart + dragged.marginTop;
    const crossAxisLayout = resolveVerticalCrossAxisLayout(containerRect, dragged, nextItem, previousItem);

    return {
        height: dragged.rect.height,
        left: crossAxisLayout.left,
        top,
        width: crossAxisLayout.width,
    };
};
